import { useState, useMemo, useEffect } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { TbAdjustmentsHorizontal } from "react-icons/tb";

const LOAN_AMOUNT = { min: 5000, max: 100000, step: 5000 };
const INTEREST_RATE = { min: 0.5, max: 1, step: 0.1 };
const LOAN_TERM = { min: 7, max: 45, step: 1 };

const RATE_AND_CHARGES = [
  "Loan Amount: ₹5,000 to ₹1,00,000",
  "Tenure: 7 to 45 Days",
  "No Pre-closure Charges",
  "No Prepayment Charges",
  "APR calculated by formula",
  "Net Monthly Salary: ≥ ₹40,000",
];

const formatINR = (n: number) => `₹${n.toLocaleString("en-IN")}`;

const clampToStep = (value: number, { min, max, step }: typeof LOAN_AMOUNT) => {
  const clamped = Math.min(max, Math.max(min, value));
  const stepped = Math.round((clamped - min) / step) * step + min;
  return Number(stepped.toFixed(2));
};

type FieldProps = {
  id: string;
  label: string;
  value: number;
  range: typeof LOAN_AMOUNT;
  format: (n: number) => string;
  onChange: (n: number) => void;
  minLabel?: string;
  maxLabel?: string;
};

/**
 * One calculator column: a typeable value box with a slider beneath it.
 * The box shows the formatted value; while focused it accepts free typing,
 * and on blur / Enter the number is clamped and snapped to the slider step.
 */
function CalcField({ id, label, value, range, format, onChange, minLabel, maxLabel }: FieldProps) {
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    setDraft(null);
  }, [value]);

  const commit = () => {
    if (draft === null) return;
    const parsed = parseFloat(draft.replace(/[^0-9.]/g, ""));
    if (!Number.isNaN(parsed)) onChange(clampToStep(parsed, range));
    setDraft(null);
  };

  const pct = ((value - range.min) / (range.max - range.min)) * 100;

  return (
    <div className="flex flex-col">
      <label htmlFor={`${id}-input`} className="text-sm text-white">
        {label}
      </label>
      <input
        id={`${id}-input`}
        type="text"
        inputMode="decimal"
        value={draft ?? format(value)}
        onFocus={(e) => {
          setDraft(String(value));
          requestAnimationFrame(() => e.target.select());
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()}
        className="mt-3 h-[52px] w-full rounded-md bg-ep-ivory px-3 font-serif text-[26px] text-ep-green outline-none ring-ep-gold focus-visible:ring-2"
      />
      <input
        id={`${id}-range`}
        type="range"
        aria-label={`${label} slider`}
        min={range.min}
        max={range.max}
        step={range.step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="ep-range mt-[34px]"
        style={{
          background: `linear-gradient(to right, #FFFFFF 0%, #FFFFFF ${pct}%, #D3D8D0 ${pct}%, #D3D8D0 100%)`,
        }}
      />
      <div className="mt-3 flex h-4 justify-between text-xs font-semibold text-white">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

export default function LoanCalculator() {
  const [loanAmount, setLoanAmount] = useState(30000);
  const [interestRate, setInterestRate] = useState(0.5);
  const [loanTerm, setLoanTerm] = useState(15);

  const { totalPayment, totalInterest } = useMemo(() => {
    const dailyInterest = (loanAmount * interestRate) / 100;
    const totalInterest = dailyInterest * loanTerm;
    return { totalPayment: loanAmount + totalInterest, totalInterest };
  }, [loanAmount, interestRate, loanTerm]);

  const summaryRows = [
    { label: "Loan Amount", value: formatINR(loanAmount) },
    { label: "Tenure", value: `${loanTerm} Days` },
    { label: "Interest Rate", value: `${interestRate}%` },
  ];

  return (
    <section id="loan-calculator" className="scroll-mt-24 bg-ep-green py-20 text-white lg:py-[110px]" aria-labelledby="loan-calculator-title">
      <div className="page-container">
        {/* Header */}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <h2 id="loan-calculator-title" className="ep-section-title text-white">
            Loan Calculator
          </h2>
          <p className="max-w-[420px] text-base leading-[1.75] text-ep-on-dark lg:mt-1 lg:text-[17px]">
            Adjust the sliders to see your daily EMI, total payment, and interest instantly.
          </p>
        </div>

        {/* Inputs */}
        <div className="mt-12 grid gap-8 md:grid-cols-3 md:gap-10 lg:mt-14">
          <CalcField
            id="loan-amount"
            label="Loan Amount"
            value={loanAmount}
            range={LOAN_AMOUNT}
            format={formatINR}
            onChange={setLoanAmount}
            minLabel="₹5,000"
            maxLabel="₹1,00,000"
          />
          <CalcField
            id="interest-rate"
            label="Interest Rate"
            value={interestRate}
            range={INTEREST_RATE}
            format={(n) => `${n}%`}
            onChange={setInterestRate}
          />
          <CalcField
            id="loan-term"
            label="Loan Term"
            value={loanTerm}
            range={LOAN_TERM}
            format={(n) => `${n} Days`}
            onChange={setLoanTerm}
            minLabel="7 Days"
            maxLabel="45 Days"
          />
        </div>

        <hr className="mt-9 border-0 border-t border-[#A5B1A8]" />

        {/* Results */}
        <div className="mt-12 grid gap-12 md:grid-cols-2 lg:mt-[50px] lg:grid-cols-3 lg:gap-10">
          {/* Loan Summary */}
          <div className="lg:px-8 lg:pt-6">
            <h3 className="font-serif text-[30px] font-normal">Loan Summary</h3>
            <dl className="mt-4">
              {summaryRows.map((row) => (
                <div key={row.label} className="flex items-end justify-between border-b border-[#A5B1A8] pb-3 pt-6">
                  <dt className="text-sm text-ep-on-dark">{row.label}</dt>
                  <dd className="text-base text-white">{row.value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-7 rounded-lg bg-ep-ivory px-5 pb-5 pt-4" aria-live="polite">
              <p className="text-sm text-ep-muted">Total Payable</p>
              <p className="mt-2 font-serif text-[40px] leading-tight text-ep-green">₹{totalPayment.toFixed(2)}</p>
            </div>
            <a
              href={import.meta.env.VITE_WEBSITE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="ep-btn ep-btn-light mt-9"
            >
              Apply for Loan
              <FiArrowUpRight aria-hidden className="text-base" />
            </a>
          </div>

          {/* Payment Breakdown */}
          <div className="lg:pl-8 lg:pt-6">
            <h3 className="font-serif text-[30px] font-normal">Payment Breakdown</h3>
            <dl className="mt-4" aria-live="polite">
              <div className="border-b border-[#A5B1A8] pb-3 pt-6">
                <dt className="text-sm text-ep-on-dark">Total Payment</dt>
                <dd className="mt-2 font-serif text-[30px] text-white">₹{totalPayment.toFixed(2)}</dd>
              </div>
              <div className="border-b border-[#A5B1A8] pb-3 pt-7">
                <dt className="text-sm text-ep-on-dark">Total Interest</dt>
                <dd className="mt-2 font-serif text-[30px] text-white">₹{totalInterest.toFixed(2)}</dd>
              </div>
            </dl>
          </div>

          {/* Rate & Charges */}
          <div className="self-start rounded-xl bg-ep-ivory p-8 text-ep-green md:col-span-2 lg:col-span-1">
            <h3 className="flex items-center gap-4 font-serif text-[28px] font-normal leading-none">
              <TbAdjustmentsHorizontal aria-hidden className="text-xl" />
              Rate &amp; Charges
            </h3>
            <ul className="mt-6 space-y-5 text-[15px] text-ep-muted">
              {RATE_AND_CHARGES.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
